/** A run of bullets, or a stretch of ordinary prose, under an optional heading. */
export interface Block {
  heading: string | null;
  bullets: string[];
  paragraphs: string[];
}

const BULLET = /^[•*-]\s+/;

/**
 * A heading is a short line that introduces bullets.
 *
 * <p>Both halves matter. "Short" keeps a sentence that happens to sit above a list from being
 * promoted, and "introduces bullets" keeps the opening line of an ordinary paragraph from being
 * promoted — which is what a naive "line without a bullet" rule would do to every description an
 * organiser writes themselves.
 */
function isHeading(line: string, next: string | undefined): boolean {
  return (
    line.length <= 40 &&
    !BULLET.test(line) &&
    !line.endsWith('.') &&
    next !== undefined &&
    BULLET.test(next)
  );
}

/**
 * Splits a description into blocks.
 *
 * <p>Deliberately tolerant. Listings we compiled follow a house shape — Format, Ages, Refreshments,
 * Open to, What the organiser requires, Tickets — but an organiser submitting their own event
 * writes whatever they like, and that has to come out looking like what they wrote rather than
 * being forced into headings it doesn't have.
 */
export function parseDescription(description: string): Block[] {
  const lines = description.split('\n');
  const blocks: Block[] = [];
  let current: Block = { heading: null, bullets: [], paragraphs: [] };
  const flush = () => {
    if (current.heading || current.bullets.length || current.paragraphs.length) blocks.push(current);
    current = { heading: null, bullets: [], paragraphs: [] };
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    if (isHeading(line, lines[i + 1]?.trim())) {
      flush();
      current.heading = line;
    } else if (BULLET.test(line)) {
      current.bullets.push(line.replace(BULLET, ''));
    } else {
      // Prose after bullets starts a new block, so a trailing sentence isn't swallowed into the
      // list above it.
      if (current.bullets.length) flush();
      current.paragraphs.push(line);
    }
  }
  flush();
  return blocks;
}
